import type { LobKey } from "src/jsonForms/preScreen/types";
import { MetricsPublisher } from "src/metrics/metrics";

import { validateSearchConfig, type ValidationError } from "./configValidator";
import { routeLineOfBusiness, SEARCH_CONFIG_REGISTRY } from "./registry";
import {
  searchConfigSource,
  SearchConfigMissingError,
  SearchConfigRetrievalError,
} from "./searchConfigSource";
import type { SearchConfig } from "./types";

/**
 * Why a retrieval failed, for the failures another attempt could clear. See
 * `SearchConfigSourceFailureCause` for `transport` and `unparseable`; `timeout`
 * is decided here, from an abort reason.
 */
export type SearchConfigRetrievalCause = "timeout" | "transport" | "unparseable";

export type SearchConfigFailure =
  | { readonly kind: "unknownRoute"; readonly pathname: string }
  | { readonly kind: "missingConfig"; readonly lob: LobKey }
  | {
      readonly kind: "invalidConfig";
      readonly lob: LobKey;
      readonly errors: readonly ValidationError[];
    }
  | {
      readonly kind: "retrieval";
      readonly lob: LobKey;
      readonly cause: SearchConfigRetrievalCause;
    };

export type SearchConfigOutcome =
  | { readonly ok: true; readonly lob: LobKey; readonly config: SearchConfig }
  | { readonly ok: false; readonly failure: SearchConfigFailure }
  /**
   * The caller stopped waiting, by navigating away or changing line of
   * business. Not a failure: there is no one left to show it to, so it renders
   * nothing.
   */
  | { readonly ok: false; readonly aborted: true; readonly lob: LobKey };

/**
 * Matches `CACHE_TTL_MS` in `featureFlagsService`, so a flag flip and a config
 * change made together become visible in the same window.
 */
export const SEARCH_CONFIG_TTL_MS = 5 * 60 * 1000;

/**
 * About twice the slowest cold start measured against the deployed operation
 * (9.7 s in alpha, 7.1 s in gamma; warm calls return in well under a second).
 * The flag service's 3 s budget is deliberately not reused: a config retrieval
 * has no default to fall back to, so failing fast only shows an error sooner,
 * and a cold start that outlives a tight budget reads as an outage.
 */
export const SEARCH_CONFIG_TIMEOUT_MS = 20_000;

interface MemoEntry {
  readonly config: SearchConfig;
  readonly storedAt: number;
}

/**
 * Validated configs, keyed by line of business. Only successes are stored, so
 * a failure is never replayed. An entry past its TTL is not deleted: it is what
 * stale-on-error serves when the refetch fails transiently.
 *
 * Within the TTL the same object is returned, which is what makes resolution
 * idempotent: the column set and order cannot drift between two mounts.
 */
const memoizedConfigs = new Map<LobKey, MemoEntry>();

type Retrieved =
  | { readonly ok: true; readonly config: SearchConfig }
  | {
      readonly ok: false;
      readonly failure: SearchConfigFailure;
      /** Whether a stale config may stand in for this failure. */
      readonly transient: boolean;
    };

/**
 * One retrieval per line of business at a time, shared by every caller asking
 * while it is in flight, so a fast route change or two tabs produce one
 * request. Removed on settle, so a retry after a failure issues a new one.
 */
const inFlightRetrievals = new Map<LobKey, Promise<Retrieved>>();

/** Clears the memo and any in-flight retrieval. For tests. */
export function clearSearchConfigMemo(): void {
  memoizedConfigs.clear();
  inFlightRetrievals.clear();
}

function isFresh(entry: MemoEntry): boolean {
  return Date.now() - entry.storedAt < SEARCH_CONFIG_TTL_MS;
}

/**
 * Read off `name` rather than tested with `instanceof`, so a `DOMException` and
 * an error class of the caller's own classify the same way.
 */
function isTimeoutReason(reason: unknown): boolean {
  return (
    typeof reason === "object" &&
    reason !== null &&
    (reason as { readonly name?: unknown }).name === "TimeoutError"
  );
}

function timeoutReason(): DOMException {
  return new DOMException("The search configuration request timed out.", "TimeoutError");
}

type Raced<T> =
  | { readonly kind: "settled"; readonly value: T }
  | { readonly kind: "aborted"; readonly reason: unknown };

/**
 * Settles with whichever comes first, the work or the abort. Racing rather than
 * trusting the work to honour the signal is what bounds a request that never
 * settles. A rejection of `work` after the abort won is absorbed here.
 */
function raceSignal<T>(work: Promise<T>, signal: AbortSignal | undefined): Promise<Raced<T>> {
  const settled = work.then((value): Raced<T> => ({ kind: "settled", value }));
  if (!signal) {
    return settled;
  }
  if (signal.aborted) {
    settled.catch(() => undefined);
    return Promise.resolve({ kind: "aborted", reason: signal.reason });
  }
  return new Promise<Raced<T>>((resolve, reject) => {
    const onAbort = (): void => resolve({ kind: "aborted", reason: signal.reason });
    signal.addEventListener("abort", onAbort, { once: true });
    settled.then(
      (result) => {
        signal.removeEventListener("abort", onAbort);
        resolve(result);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
}

function retrievalFailure(lob: LobKey, cause: SearchConfigRetrievalCause): Retrieved {
  return { ok: false, failure: { kind: "retrieval", lob, cause }, transient: true };
}

function classifySourceError(lob: LobKey, error: unknown): Retrieved {
  if (error instanceof SearchConfigMissingError) {
    // A configuration state rather than a defect, so it is not logged.
    return { ok: false, failure: { kind: "missingConfig", lob: error.lob }, transient: false };
  }
  // Logged because each of these is an operational or code defect someone has
  // to read to fix. The never-throws guarantee is structural rather than
  // inherited from the source: an unexpected throw is still a reported failure.
  console.error(error);
  if (error instanceof SearchConfigRetrievalError) {
    return retrievalFailure(lob, error.retrievalCause);
  }
  return retrievalFailure(lob, "transport");
}

/**
 * The timer lives here rather than with the caller because the request is
 * shared. A caller's own timeout could only abandon its wait, leaving a hung
 * request in flight for the next caller, and the retry it offers, to join.
 * Timing out the shared request instead ends it for everyone waiting on it,
 * and removes it, so a retry starts clean.
 */
async function runRetrieval(lob: LobKey): Promise<Retrieved> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(timeoutReason()), SEARCH_CONFIG_TIMEOUT_MS);
  try {
    const raced = await raceSignal(
      searchConfigSource.load(lob, controller.signal),
      controller.signal
    );
    // Nothing but the timer aborts this controller.
    if (raced.kind === "aborted") {
      return retrievalFailure(lob, "timeout");
    }
    const validation = validateSearchConfig(raced.value);
    if (!validation.ok) {
      return {
        ok: false,
        failure: { kind: "invalidConfig", lob, errors: validation.errors },
        transient: false,
      };
    }
    memoizedConfigs.set(lob, { config: validation.config, storedAt: Date.now() });
    return { ok: true, config: validation.config };
  } catch (error) {
    return classifySourceError(lob, error);
  } finally {
    clearTimeout(timer);
  }
}

function sharedRetrieval(lob: LobKey): Promise<Retrieved> {
  const existing = inFlightRetrievals.get(lob);
  if (existing) {
    return existing;
  }
  const attempt = runRetrieval(lob);
  inFlightRetrievals.set(lob, attempt);
  void attempt.then(() => {
    if (inFlightRetrievals.get(lob) === attempt) {
      inFlightRetrievals.delete(lob);
    }
  });
  return attempt;
}

/**
 * A transient failure over a config this tab has already resolved serves that
 * config rather than an error. Deterministic failures, a missing or rejected
 * document, are not masked: they are server states their author needs to see.
 */
function failOrServeStale(
  lob: LobKey,
  startedAt: number,
  retrieved: Retrieved
): SearchConfigOutcome {
  if (retrieved.ok) {
    publishRetrievalMetric(lob, "success", startedAt, retrieved.config);
    return { ok: true, lob, config: retrieved.config };
  }
  const stale = memoizedConfigs.get(lob);
  if (retrieved.transient && stale) {
    publishRetrievalMetric(lob, "stale", startedAt, stale.config);
    return { ok: true, lob, config: stale.config };
  }
  publishRetrievalMetric(lob, "failure", startedAt);
  return { ok: false, failure: retrieved.failure };
}

/**
 * The reason, not the abort, decides the outcome. A caller that stopped waiting
 * because too much time passed gets a retryable error; one that navigated away
 * gets nothing, because there is no one left to show it to.
 */
function settleCallerAbort(lob: LobKey, startedAt: number, reason: unknown): SearchConfigOutcome {
  if (isTimeoutReason(reason)) {
    return failOrServeStale(lob, startedAt, retrievalFailure(lob, "timeout"));
  }
  publishRetrievalMetric(lob, "abort", startedAt);
  return { ok: false, aborted: true, lob };
}

/**
 * Resolves the Search_Config for a claims history route.
 *
 * Never throws. An unregistered route, a missing or rejected document, a failed
 * or timed-out retrieval, and an abort all return an outcome, so a
 * configuration problem cannot take the page down.
 *
 * `signal` belongs to the caller and only ends the caller's wait. It does not
 * cancel the retrieval, which other callers may share and which, if it
 * completes, warms the memo for the next mount.
 *
 * Publishes exactly one retrieval metric per call, labelled `success`, `stale`,
 * `failure`, or `abort`. The one exception is an unregistered route, which
 * resolves no line of business and attempts no retrieval.
 */
export async function resolveSearchConfig(
  pathname: string,
  signal?: AbortSignal
): Promise<SearchConfigOutcome> {
  const lob = routeLineOfBusiness(pathname);
  if (lob === null) {
    return { ok: false, failure: { kind: "unknownRoute", pathname } };
  }

  const startedAt = Date.now();
  try {
    const memoized = memoizedConfigs.get(lob);
    if (memoized && isFresh(memoized)) {
      publishRetrievalMetric(lob, "success", startedAt, memoized.config);
      return { ok: true, lob, config: memoized.config };
    }
    const raced = await raceSignal(sharedRetrieval(lob), signal);
    if (raced.kind === "aborted") {
      return settleCallerAbort(lob, startedAt, raced.reason);
    }
    return failOrServeStale(lob, startedAt, raced.value);
  } catch (error) {
    console.error(error);
    return failOrServeStale(lob, startedAt, retrievalFailure(lob, "transport"));
  }
}

type RetrievalOutcomeLabel = "success" | "stale" | "failure" | "abort";

/**
 * Declared by every shipped config, so it only applies where no document is
 * reachable to declare it — a missing registry entry, or a retrieval that failed
 * before returning anything.
 */
const FALLBACK_RETRIEVAL_PUBLISHER_NAME = "claimsHistory.resolveSearchConfig";

/**
 * One timer per call. The publisher name is the namespace and the metric name
 * carries the line of business and the outcome, because a timer publishes a
 * duration and nothing else.
 *
 * `abort` is published although the user never sees an abort, so a stage where
 * people navigate away from a slow page is observable. `stale` is published so
 * the transient failures stale-on-error hides from users are not hidden from us.
 *
 * Wrapped, because publishing is fire-and-forget: a metrics outage must not
 * present as a claims outage, and must not turn a resolved config into a
 * failure.
 */
function publishRetrievalMetric(
  lob: LobKey,
  outcome: RetrievalOutcomeLabel,
  startedAt: number,
  config?: SearchConfig
): void {
  const durationMs = Date.now() - startedAt;
  try {
    const publisherName =
      config?.metrics.configRetrievalPublisherName ??
      SEARCH_CONFIG_REGISTRY[lob]?.searchConfig.metrics.configRetrievalPublisherName ??
      FALLBACK_RETRIEVAL_PUBLISHER_NAME;
    new MetricsPublisher(publisherName).publishNamedTimerMetric(`${lob}.${outcome}`, durationMs);
  } catch (error) {
    console.error(error);
  }
}
